import React from 'react';
import { AlertTriangle, FolderOpen, FileDown, Save, X } from 'lucide-react';

/**
 * A save that did not happen, and the ways out of it.
 *
 * This exists because of one specific, badly-behaved case: a Google Drive
 * folder accepts the write, reports success, and produces no file. Any dialog
 * here has to do two things — say why, and make sure the work still gets out
 * of the browser. Hence "Download instead": whatever else the instructor
 * decides, they leave with their deck.
 *
 * @param {object} props
 * @param {{ title: string, summary: string, detail: string, suggestions: string[] }} props.problem
 * @param {() => void} props.onChooseFolder
 * @param {() => void} props.onSaveAs
 * @param {() => void} props.onDownload
 * @param {() => void} props.onDismiss
 * @param {boolean} [props.busy] - A retry is in flight
 */
export default function SaveProblemDialog({
  problem,
  onChooseFolder,
  onSaveAs,
  onDownload,
  onDismiss,
  busy = false
}) {
  if (!problem) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="save-problem-title"
    >
      <div className="my-8 max-h-[calc(100vh-4rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-4 rounded-t-2xl border-b border-gray-200 bg-amber-50 px-6 py-5">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="save-problem-title" className="text-xl font-semibold text-gray-900">
              {problem.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg p-2 text-gray-500 hover:bg-amber-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <p className="text-sm leading-relaxed text-gray-700">{problem.summary}</p>

          {problem.suggestions?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">What to do</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {problem.suggestions.map((suggestion, index) => (
                  <li key={`${suggestion}-${index}`}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {problem.detail && (
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">Technical details</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-gray-600">
                <code>{problem.detail}</code>
              </pre>
            </details>
          )}
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="order-last rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 sm:order-first sm:mr-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            <FileDown size={16} />
            Download instead
          </button>
          <button
            type="button"
            onClick={onSaveAs}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            <Save size={16} />
            Save as…
          </button>
          <button
            type="button"
            onClick={onChooseFolder}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            <FolderOpen size={16} />
            Choose another folder
          </button>
        </div>
      </div>
    </div>
  );
}
